import type {
  SalesDemoContractStatus,
  SalesDemoPotentialSubscriptionType,
  SalesDemoSource,
  SalesDemoStatus,
} from '@swipe4work/models-orval'

/*
 * Date types, helpers and preset builders now live in shared/utils so non-sales
 * features can reuse them. Re-exported here for backwards compatibility.
 */
export {
  addDays,
  addMonths,
  buildForecastRangePresets,
  buildStatisticsRangePresets,
  endOfDay,
  endOfIsoWeek,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  getIsoWeekNumber,
  getQuarter,
  startOfDay,
  startOfIsoWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  toDateOnlyString,
  type DateRange,
  type DateRangePreset,
} from '@/shared/utils/dateRangePresets'

export type SalesPeriodGroupBy = 'day' | 'week' | 'month' | 'quarter' | 'year'

interface SalesPeriodGroupByOption {
  label: string
  value: SalesPeriodGroupBy
}

// ─── Status / subscription helpers ───────────────────────────────────────

export type SalesStatusSeverity = 'info' | 'warn' | 'success' | 'danger' | 'secondary'

export interface SalesStatusOption {
  label: string
  value: SalesDemoStatus
  severity: SalesStatusSeverity
}

/*
 * Ordered as the sales pipeline flows: Gepland → Gesprek Gevoerd → Schriftelijk Akkoord →
 * Interesse Later → Deal Won, with Deal Lost as the drop-out.
 */
export const statusOptions: SalesStatusOption[] = [
  {
    label: 'Gepland',
    value: 'Planned',
    severity: 'info',
  },
  {
    label: 'Gesprek Gevoerd',
    value: 'ConversationHeld',
    severity: 'info',
  },
  {
    label: 'Schriftelijk Akkoord',
    value: 'WrittenAgreement',
    severity: 'warn',
  },
  {
    label: 'Interesse Later',
    value: 'InterestLater',
    severity: 'secondary',
  },
  {
    label: 'Deal Won',
    value: 'DealWon',
    severity: 'success',
  },
  {
    label: 'Deal Lost',
    value: 'DealLost',
    severity: 'danger',
  },
]

/**
 * Statuses that commit the customer to a deal: the contracted subscription and the expected
 * onboarding date have to be known, and the sales forecast is built from exactly these.
 */
export const committedStatuses: SalesDemoStatus[] = [ 'WrittenAgreement', 'DealWon' ]

export function isCommittedStatus(status: SalesDemoStatus): boolean {
  return committedStatuses.includes(status)
}

export function getStatusLabel(status: SalesDemoStatus): string {
  return statusOptions.find(o => o.value === status)?.label ?? status
}

export function getStatusSeverity(status: SalesDemoStatus): SalesStatusSeverity {
  return statusOptions.find(o => o.value === status)?.severity ?? 'secondary'
}

export interface ContractStatusOption {
  label: string
  value: SalesDemoContractStatus
  severity: SalesStatusSeverity
}

/**
 * A deal stops counting as merely "contracted" once its first invoice goes out — Realized then
 * replaces it, regardless of the demo's pipeline Status (which stays WrittenAgreement/DealWon forever).
 */
export const contractStatusOptions: ContractStatusOption[] = [
  {
    label: 'Nog niet',
    value: 'NotYet',
    severity: 'secondary',
  },
  {
    label: 'Contracted',
    value: 'Contracted',
    severity: 'warn',
  },
  {
    label: 'Gerealiseerd',
    value: 'Realized',
    severity: 'success',
  },
]

export function getContractStatusLabel(status: SalesDemoContractStatus): string {
  return contractStatusOptions.find(o => o.value === status)?.label ?? status
}

export function getContractStatusSeverity(status: SalesDemoContractStatus): SalesStatusSeverity {
  return contractStatusOptions.find(o => o.value === status)?.severity ?? 'secondary'
}

export interface SalesSourceOption {
  label: string
  value: SalesDemoSource
}

/**
 * Where a demo came from. Only outbound demos are earned by calling, so they are the only ones the
 * Gebeld → Gesprekken → Demo conversion counts; the rest is logged and reported per channel.
 */
export const sourceOptions: SalesSourceOption[] = [
  {
    label: 'Outbound demo',
    value: 'Outbound',
  },
  {
    label: 'Demo via WingM',
    value: 'WingM',
  },
  {
    label: 'Inbound lead via websiteformulier',
    value: 'InboundWebsiteForm',
  },
  {
    label: 'Anders',
    value: 'Other',
  },
]

/** Channels entered often enough to offer as a suggestion behind the "Anders" free-text field. */
export const sourceOtherSuggestions: string[] = [ 'LinkedIn', 'Werff&' ]

/**
 * Label for a demo's channel: the free-text channel for an "Anders" demo, otherwise the source's own
 * label. Demos that predate the field carry no source at all.
 */
export function getSourceLabel(
  source: SalesDemoSource | null | undefined,
  sourceOther?: string | null,
): string {
  if (!source) {
    return 'Onbekend'
  }
  if (source === 'Other') {
    return sourceOther?.trim() || 'Anders'
  }

  return sourceOptions.find(o => o.value === source)?.label ?? source
}

export function getSourceSeverity(source: SalesDemoSource | null | undefined): SalesStatusSeverity {
  switch (source) {
    case 'Outbound': return 'success'
    case 'WingM': return 'info'
    case 'InboundWebsiteForm': return 'warn'

    default: return 'secondary'
  }
}

export interface SubscriptionTypeOption {
  label: string
  value: SalesDemoPotentialSubscriptionType
}

export const subscriptionTypeOptions: SubscriptionTypeOption[] = [
  {
    label: 'Match Credits',
    value: 'MatchCredits',
  },
  {
    label: 'Vacature Slots',
    value: 'VacancySlots',
  },
]

/**
 * Label for the subscription or credit package a company actually chose during onboarding.
 * Null while the deal has not been closed yet.
 */
export function getChosenDealLabel(demo: {
  chosenSubscriptionTitle?: string | null
  chosenCreditPackageName?: string | null
  chosenMatchCredits?: number | null
}): string | null {
  if (demo.chosenSubscriptionTitle) {
    return demo.chosenSubscriptionTitle
  }
  if (demo.chosenCreditPackageName || demo.chosenMatchCredits != null) {
    const credits = demo.chosenMatchCredits != null ? `${demo.chosenMatchCredits} match credits` : null

    return [ demo.chosenCreditPackageName, credits ].filter(Boolean).join(' — ')
  }

  return null
}

/**
 * Initials for a sales person's display name: first + last word initial (e.g. "Joey Roeters" → "JR").
 */
export function salesPersonInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return '?'
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase()
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

export function formatLeadTimeDays(days: number | null | undefined): string {
  if (days == null) {
    return '—'
  }
  if (Math.abs(days) < 1) {
    const hours = Math.round(days * 24 * 10) / 10

    return `${hours} uur`
  }

  return `${days.toFixed(1)} dgn`
}

export const groupByPeriodOptions: SalesPeriodGroupByOption[] = [
  {
    label: 'Dag',
    value: 'day',
  },
  {
    label: 'Week',
    value: 'week',
  },
  {
    label: 'Maand',
    value: 'month',
  },
  {
    label: 'Kwartaal',
    value: 'quarter',
  },
  {
    label: 'Jaar',
    value: 'year',
  },
]
